import { useCallback } from 'react';

import { C_SMART_MODAL_BEHAVIORS } from '@/ts/constants/C_Overlay';
import { T_SmartModalProps } from '@/ts/types/T_Overlay';

import { useDeviceDetection } from './useDeviceDetection';

interface I_UseSmartModalResult {
  modalType: 'dialog' | 'sheet';
  modalPosition: 'center' | 'bottom' | 'right';
  content: React.ReactNode;
  shouldUseSheet: boolean;
  shouldUseDialog: boolean;
}

export function useSmartModal({
  dialogType = 'auto',
  behavior,
  children,
  mobileContent,
  desktopContent,
  tabletContent,
  fallbackContent
}: Pick<
  T_SmartModalProps,
  | 'dialogType'
  | 'behavior'
  | 'children'
  | 'mobileContent'
  | 'desktopContent'
  | 'tabletContent'
  | 'fallbackContent'
>): I_UseSmartModalResult {
  const { deviceType, isMobile, isTablet, isDesktop } = useDeviceDetection();

  // Determine modal behavior based on dialogType and device
  const getModalBehavior = useCallback((): {
    type: 'dialog' | 'sheet';
    position: 'center' | 'bottom' | 'right';
  } => {
    // If custom behavior is provided, use it
    if (behavior) {
      const deviceBehavior = behavior[deviceType];
      if (deviceBehavior) {
        return {
          type: deviceBehavior.type,
          position:
            deviceBehavior.position ||
            (deviceBehavior.type === 'dialog' ? 'center' : 'bottom')
        };
      }
    }

    // Use predefined behaviors based on dialogType
    if (dialogType === 'dialog') {
      return (
        C_SMART_MODAL_BEHAVIORS.dialog[deviceType] ||
        C_SMART_MODAL_BEHAVIORS.dialog.desktop
      );
    }

    if (dialogType === 'sheet') {
      return (
        C_SMART_MODAL_BEHAVIORS.sheet[deviceType] ||
        C_SMART_MODAL_BEHAVIORS.sheet.desktop
      );
    }

    if (dialogType === 'sheetCenter') {
      return (
        C_SMART_MODAL_BEHAVIORS.sheetCenter[deviceType] ||
        C_SMART_MODAL_BEHAVIORS.sheetCenter.desktop
      );
    }

    if (dialogType === 'auto') {
      // Default to sheet for mobile, dialog for desktop
      return isMobile
        ? { type: 'sheet', position: 'bottom' }
        : { type: 'dialog', position: 'center' };
    }

    // Fallback
    return { type: 'dialog', position: 'center' };
  }, [dialogType, behavior, deviceType, isMobile]);

  // Get content for current device
  const getContent = useCallback((): React.ReactNode => {
    if (isMobile && mobileContent) {
      return mobileContent;
    }

    if (isDesktop && desktopContent) {
      return desktopContent;
    }

    if (isTablet && tabletContent) {
      return tabletContent;
    }

    if (fallbackContent) {
      return fallbackContent;
    }

    return children;
  }, [
    isMobile,
    isDesktop,
    isTablet,
    mobileContent,
    desktopContent,
    tabletContent,
    fallbackContent,
    children
  ]);

  // Calculate modal behavior and content directly
  const modalBehavior = getModalBehavior();
  const content = getContent();

  return {
    modalType: modalBehavior.type,
    modalPosition: modalBehavior.position,
    content,
    shouldUseSheet: modalBehavior.type === 'sheet',
    shouldUseDialog: modalBehavior.type === 'dialog'
  };
}
